import React, { useState, useMemo, useCallback } from 'react';
import { Col, Row, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { NavLink, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useCreateActivityMutation } from '../../../features/authentication/activityApiSlice';
import { useData } from '../../assignments/DataContext';
import DropDown from '../../../components/DropDown';
import FolderDropdown from '../../../components/FolderDropdown';
import CustomToast from '../../../components/Toast';
import ActivityTypes from '../../../enums/activityTypes';
import '../../../../src/styles/globalStyle.scss';

const SHARED_FOLDER = '*** Shared Assignments ***';
const DEFAULT_FOLDER_NAME = 'Default Folder';

const ActivityCreateContent = ({showBrowseContent, showTitle, onCreateSuccess, fromActivities, handleCancel}) => {
  const [createActivity, { isLoading }] = useCreateActivityMutation();
  const { courseData = [], folderData = [] } = useData();
  const navigate = useNavigate();

  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState(0);
  const [selectedFolderName, setSelectedFolderName] = useState(DEFAULT_FOLDER_NAME);
  const [enteredName, setEnteredName] = useState('');
  const [validCourseSelected, setValidCourseSelected] = useState(true);
  const [validName, setValidName] = useState(true);

  const allCourses = useMemo(
    () => [{ id: 0, name: 'Select Course' }, ...courseData],
    [courseData]
  );

  const { allFolders, allParents } = useMemo(() => {
    const filteredFolder = folderData.filter(
      (item) =>
        item.folderID !== -88 &&
        !item.description.toLowerCase().includes(SHARED_FOLDER.toLowerCase())
    );

    const mappedFolders = filteredFolder.map((item) => ({
      id: item.folderID,
      name: item.description,
      parent: item.parent
    }));

    const parents = [
      { id: -1, name: 'No Parent' },
      ...mappedFolders.filter((item) => item.parent === 0).map(({ id, name }) => ({ id, name }))
    ];

    return {
      allFolders: mappedFolders.map(({ id, name }) => ({ id, name })),
      allParents: parents
    };
  }, [folderData]);

  const handleSelectCourse = useCallback((event) => {
    const id = Number(event.target.value);
    setSelectedCourseId(id);
    setValidCourseSelected(id > 0);
  }, []);

  const handleCreatePublicFlashcard = useCallback(() => {
    CustomToast('Public Flashcard creation is not available yet.', 'info');
  }, []);

  const handleClear = useCallback(() => {
    setSelectedCourseId('');
    setSelectedFolderId(0);
    setEnteredName('');
    setSelectedFolderName(DEFAULT_FOLDER_NAME);
    setValidCourseSelected(true);
    setValidName(true);
  }, []);

  const handleCreate = useCallback(async () => {
    const nameIsValid = enteredName.trim().length > 0;
    const courseIsValid = selectedCourseId > 0;

    setValidName(nameIsValid);
    setValidCourseSelected(courseIsValid);

    if (!nameIsValid || !courseIsValid || isLoading) return;

    const payload = {
      CourseId: selectedCourseId,
      Description: enteredName.trim(),
      FolderId: selectedFolderId,
      Activity: ActivityTypes.FlashCard
    };

    try {
      const data = await createActivity(payload).unwrap();
      CustomToast('Flashcard Created Successfully', 'success');
      handleClear();

      if (onCreateSuccess) {
        onCreateSuccess(data.assignmentId);
        return;
      }

      navigate(`/activitiesSearch/activities/${data.assignmentId}/castle_activities/vocabularies`, {
        replace: true
      });
    } catch (error) {
      console.log(error);
    }
  }, [
    enteredName,
    selectedCourseId,
    selectedFolderId,
    isLoading,
    createActivity,
    navigate,
    onCreateSuccess,
    handleClear
  ]);

  return (
    <>
      {showTitle && (
        <div className="row align-items-center mb-4">
          <div className="col-lg-5">
            <h4 className="secondary-title mb-lg-0">Create New Flashcard</h4>
          </div>
        </div>
      )}

      <Row>
        <Col className={showBrowseContent ? 'col-xl-8' : ''}>
          <Row>
            <Col className={`col-md-${fromActivities ? '4' : '6'} mb-3`}>
              <DropDown
                label="Select Course"
                value={allCourses}
                onChangeHandler={handleSelectCourse}
                selectedValue={selectedCourseId}
                showValidation={!validCourseSelected}
                dropDownName="Course"
                labelClassName="fs-14"
              />
            </Col>

            <Col className={`col-md-${fromActivities ? '4' : '6'} mb-3`}>
              <Form.Group>
                <Form.Label htmlFor="classDropdown" className="fs-14">Select Folder</Form.Label>
                <FolderDropdown
                  allFolders={allFolders}
                  allParents={allParents}
                  dropdownId="folderList"
                  selectedId={selectedFolderId}
                  setSelectedFolderId={setSelectedFolderId}
                  selectedFolderName={selectedFolderName}
                  setSelectedFolderName={setSelectedFolderName}
                  activityType ={ActivityTypes.FlashCard}
                  selectedValClassName="fs-14"
                />
              </Form.Group>
            </Col>
            <Col className={`col-md-${fromActivities ? '4' : '12'} mb-3`}>
              <label className="form-label fs-14">Flashcard Name</label>
              <input
                type="text"
                className={`form-control ${!validName ? 'border-danger' : ''}`}
                value={enteredName}
                onChange={(e) => setEnteredName(e.target.value)}
              />
              {!validName && (
                <p className="text-danger">Flashcard Name Is Required.</p>
              )}
            </Col>
          </Row>

          <Row>
            <Col className="col-8 d-flex flex-column flex-sm-row gap-3 align-items-center">
              {showTitle && (
                <div className="d-flex flex-wrap gap-3">
                  <button
                    className="btn-link secondary-link px-0 text-start"
                    onClick={handleCreatePublicFlashcard}
                  >
                    <i className="fa-solid fa-layer-group me-2"></i>
                    Create From Public Flashcards
                  </button>
                </div>
              )}
            </Col>
            <Col className="pt-2">
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-castleWhite min-w-150" onClick={fromActivities ? handleClear : handleCancel}>
                  {fromActivities ? 'Clear' : 'Cancel'}
                </button>
                <button className="btn btn-castleRed min-w-150" onClick={handleCreate}>
                    Create
                </button>
              </div>
            </Col>
          </Row>
        </Col>

        {showBrowseContent && (
          <Col className="col-xl-4 mt-4 mt-xl-0">
            <NavLink
              to="/questionsSearch"
              className="select-questions h-100 p-4 d-flex flex-column align-items-center justify-content-center text-center text-decoration-none"
            >
              <span className="mb-3 d-flex align-items-center justify-content-center nav-link-browse-content">
                <FontAwesomeIcon color="#82A9E2" icon={faFileMagnifyingGlass} />
              </span>
              <h4>Browse Content</h4>
              <p className="mb-0">Select the vocabulary first, then create a new flashcard.</p>
            </NavLink>
          </Col>
        )}
      </Row>
    </>
  );
};

ActivityCreateContent.propTypes = {
  showBrowseContent: PropTypes.bool,
  showTitle: PropTypes.bool,
  onCreateSuccess: PropTypes.func,
  fromActivities: PropTypes.bool,
  handleCancel: PropTypes.func
};

export default ActivityCreateContent;